import Fetch from '@/utils/Fetch';
import DownloadService from '@/utils/download-service';
import { ApiError, ImportDraft, ImportDraftCommitResult, ImportDraftPreview, ImportRecipe, SourceSample } from './import-draft-model';

export interface CreateDraftInput {
  file: File;
  importType: number;
  instruction?: string;
  allowGeneratedCodes?: boolean;
  mode?: number;
}

export function isApiError(value: any): value is ApiError {
  return !!value && typeof value === 'object' && typeof value.status === 'number' && value.status !== 200;
}

export function errorMessage(value: any, fallback: string): string {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  return value.Message || value.serverMessage || value.message || fallback;
}

export const REINFER_BUSY_MESSAGES: Record<number, string> = {
  409: 'This file is already being processed. Wait for it to finish, or cancel it first.',
  503: 'The server is busy. Try again in a moment.',
};

function plainServerText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim().replace(/^"(.*)"$/s, '$1').trim();
  if (!text || text.startsWith('{') || text.startsWith('<')) return null;
  return text;
}

export function reinferRefusal(value: any): string | null {
  if (!isApiError(value)) return null;
  const fallback = REINFER_BUSY_MESSAGES[value.status];
  if (!fallback) return null;
  return value.Message || plainServerText(value.serverMessage) || fallback;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onloadend = () => resolve(String(reader.result).replace(/^data:.*;base64,/, ''));
    reader.readAsDataURL(file);
  });
}

async function create(input: CreateDraftInput): Promise<ImportDraft | ApiError> {
  const base64 = await readAsBase64(input.file);
  return Fetch.post({
    url: '/ImportDraft',
    params: {
      FileName: input.file.name,
      FileBase64: base64,
      ImportType: input.importType,
      Instruction: input.instruction || null,
      AllowGeneratedCodes: !!input.allowGeneratedCodes,
      MaskSamples: false,
      Mode: input.mode ?? 0,
    },
  });
}

function get(draftID: string): Promise<ImportDraft | ApiError> {
  return Fetch.get({ url: `/ImportDraft/${draftID}` });
}

function list(take = 50): Promise<ImportDraft[] | ApiError> {
  return Fetch.get({ url: '/ImportDraft/List', params: { take } });
}

function reinfer(draftID: string, instruction: string | undefined, allowGeneratedCodes: boolean, mode = 0): Promise<ImportDraft | ApiError> {
  return Fetch.post({
    url: `/ImportDraft/${draftID}/Reinfer`,
    params: { Instruction: instruction || null, AllowGeneratedCodes: allowGeneratedCodes, MaskSamples: false, Mode: mode },
  });
}

function updateRecipe(draftID: string, recipe: ImportRecipe): Promise<ImportDraft | ApiError> {
  return Fetch.put({ url: `/ImportDraft/${draftID}/Recipe`, params: { Recipe: recipe } });
}

function preview(draftID: string, pageIndex: number, pageSize: number, issuesOnly: boolean): Promise<ImportDraftPreview | ApiError> {
  return Fetch.get({ url: `/ImportDraft/${draftID}/Preview`, params: { pageIndex, pageSize, issuesOnly }, cache: 'no-store' });
}

function commit(draftID: string): Promise<ImportDraftCommitResult | ApiError> {
  return Fetch.post({ url: `/ImportDraft/${draftID}/Commit`, params: null });
}

function cancel(draftID: string): Promise<any> {
  return Fetch.post({ url: `/ImportDraft/${draftID}/Cancel`, params: null });
}

function sourceSample(draftID: string, rows = 8, skip = 0): Promise<SourceSample | ApiError> {
  return Fetch.get({ url: `/ImportDraft/${draftID}/Source`, params: { rows, skip } });
}

let inlineProcessing: Promise<boolean> | null = null;

function inlineProcessingEnabled(): Promise<boolean> {
  if (!inlineProcessing) {
    inlineProcessing = Fetch.get({ url: '/Import/InlineProcessing' })
      .then((result: any) => !isApiError(result) && result?.Enabled === true)
      .catch(() => false);
  }
  return inlineProcessing;
}

function processImportNow(importID: string): Promise<{ Started: boolean } | ApiError> {
  return Fetch.post({ url: `/Import/${importID}/ProcessNow`, params: null });
}

function downloadReview(draftID: string, complete?: () => void): Promise<void> {
  return DownloadService.downloadFile('GET', `/ImportDraft/${draftID}/Review`, null, false, false, '', '', null, false, complete || null);
}

export default {
  create,
  get,
  list,
  reinfer,
  updateRecipe,
  preview,
  commit,
  cancel,
  sourceSample,
  downloadReview,
  inlineProcessingEnabled,
  processImportNow,
};
